import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { db, adminOrderQrToolkitCall } from '../firebase';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import './OrderList.css';

const formatOrderDate = (date) => {
  const value = date?.toDate ? date.toDate() : null;
  return value ? value.toLocaleString('es-AR') : 'Sin fecha';
};

const formatCurrency = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0
  }).format(value);
};

const getOrderTotal = (order) => {
  const possibleTotals = [order.totalPrice, order.total, order.amount, order.subtotal];
  return possibleTotals.find((value) => typeof value === 'number' && !Number.isNaN(value));
};

const OrderList = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleOrders, setVisibleOrders] = useState(10);
  const [busyOrderId, setBusyOrderId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setIsLoading(true);
        setErrorMessage('');
        const q = query(collection(db, 'compras_mercadopago'), orderBy('date', 'desc'));
        const querySnapshot = await getDocs(q);
        const ordersList = querySnapshot.docs.map((d) => ({
          id: d.id,
          ...d.data()
        }));
        setOrders(ordersList);
        setFilteredOrders(ordersList);
      } catch (error) {
        console.error('Error fetching orders:', error);
        setErrorMessage('No se pudo cargar el historial de ventas. Intentá nuevamente en unos minutos.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const patchOrderInState = useCallback((orderId, patch) => {
    setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, ...patch } : o)));
    setFilteredOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, ...patch } : o)));
  }, []);

  const filterOrders = useCallback(() => {
    let filtered = orders;

    if (startDate && endDate) {
      filtered = filtered.filter((order) => {
        const raw = order.date;
        const orderDate = raw?.toDate ? raw.toDate() : null;
        if (!orderDate) return false;
        const from = new Date(startDate);
        const to = new Date(endDate);
        to.setHours(23, 59, 59, 999);
        return orderDate >= from && orderDate <= to;
      });
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((order) => {
        const num = order.orderNumber != null ? String(order.orderNumber) : '';
        const name = order.nombre ? String(order.nombre).toLowerCase() : '';
        return num.includes(searchQuery) || name.includes(q);
      });
    }

    setFilteredOrders(filtered);
    setVisibleOrders(10);
  }, [orders, startDate, endDate, searchQuery]);

  useEffect(() => {
    filterOrders();
  }, [filterOrders]);

  const toggleOrderDetails = (orderId) => {
    setSelectedOrder(selectedOrder === orderId ? null : orderId);
  };

  const loadMoreOrders = () => {
    setVisibleOrders((v) => v + 10);
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSearchQuery('');
  };

  const handleGenerateQrs = async (order) => {
    setBusyOrderId(order.id);
    try {
      const data = await adminOrderQrToolkitCall(order.id, 'generateQrs');
      if (data?.cartItems && Array.isArray(data.cartItems)) {
        patchOrderInState(order.id, { cartItems: data.cartItems });
      }
      alert('QR generados y guardados en la orden y en el índice.');
    } catch (e) {
      alert(e.message || 'No se pudieron generar los QR.');
    } finally {
      setBusyOrderId(null);
    }
  };

  const handleResendEmail = async (order) => {
    setBusyOrderId(order.id);
    try {
      await adminOrderQrToolkitCall(order.id, 'resendPurchaseEmail');
      patchOrderInState(order.id, { purchaseConfirmationEmailSent: true });
      alert('Correo reenviado al cliente (y copia al administrador).');
    } catch (e) {
      alert(e.message || 'No se pudo reenviar el correo.');
    } finally {
      setBusyOrderId(null);
    }
  };

  const orderNeedsQr = useCallback((order) => (order.cartItems || []).some((item) => !item.qrValue), []);

  const stats = useMemo(() => {
    const paidOrders = filteredOrders.filter((order) => order.isPaid).length;
    const pendingQr = filteredOrders.filter((order) => orderNeedsQr(order)).length;
    const products = filteredOrders.reduce(
      (acc, order) =>
        acc + (order.cartItems || []).reduce((itemsAcc, item) => itemsAcc + Number(item.quantity || 0), 0),
      0
    );

    return {
      paidOrders,
      pendingQr,
      products
    };
  }, [filteredOrders, orderNeedsQr]);

  return (
    <div className="order-history">
      <header className="order-history__hero">
        <div>
          <p className="order-history__eyebrow">Mercado Pago</p>
          <h2>Historial de ventas</h2>
          <p className="order-history__lead">
            Revisá ventas acreditadas, completá QR pendientes y reenviá el correo de compra desde un panel más claro.
          </p>
        </div>
        <div className="order-history__summary" aria-label="Resumen del historial filtrado">
          <article className="order-history__stat">
            <span>{filteredOrders.length}</span>
            <small>Órdenes</small>
          </article>
          <article className="order-history__stat">
            <span>{stats.paidOrders}</span>
            <small>Pagadas</small>
          </article>
          <article className="order-history__stat">
            <span>{stats.pendingQr}</span>
            <small>QR pendientes</small>
          </article>
          <article className="order-history__stat">
            <span>{stats.products}</span>
            <small>Productos</small>
          </article>
        </div>
      </header>

      <section className="order-history__filters" aria-label="Filtros del historial de ventas">
        <label className="order-history__field">
          <span>Desde</span>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </label>
        <label className="order-history__field">
          <span>Hasta</span>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </label>
        <label className="order-history__field order-history__field--search">
          <span>Buscar</span>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Número de orden o nombre"
          />
        </label>
        <button type="button" className="order-history__ghost-btn" onClick={clearFilters}>
          Limpiar filtros
        </button>
      </section>

      {errorMessage && <p className="order-history__notice order-history__notice--danger">{errorMessage}</p>}

      {isLoading ? (
        <p className="order-history__empty">Cargando historial de ventas...</p>
      ) : filteredOrders.length === 0 ? (
        <p className="order-history__empty">No hay ventas que coincidan con los filtros actuales.</p>
      ) : (
        <ul className="order-history__list">
          {filteredOrders.slice(0, visibleOrders).map((order) => {
            const isOpen = selectedOrder === order.id;
            const total = getOrderTotal(order);

            return (
              <li key={order.id} className={`order-history__card ${isOpen ? 'is-open' : ''}`}>
                <div className="order-history__card-main">
                  <div className="order-history__order-title">
                    <span className="order-history__order-number">Orden #{order.orderNumber || 'sin número'}</span>
                    <span className={`order-history__badge ${order.isPaid ? 'is-paid' : 'is-unpaid'}`}>
                      {order.isPaid ? 'Pagado' : 'No pagado'}
                    </span>
                  </div>

                  <div className="order-history__meta-grid">
                    <span>
                      <small>Cliente</small>
                      {order.nombre || 'Sin nombre'}
                    </span>
                    <span>
                      <small>Fecha</small>
                      {formatOrderDate(order.date)}
                    </span>
                    <span>
                      <small>Total</small>
                      {formatCurrency(total)}
                    </span>
                    <span>
                      <small>Productos</small>
                      {(order.cartItems || []).length}
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  className="order-history__toggle"
                  onClick={() => toggleOrderDetails(order.id)}
                  aria-expanded={isOpen}
                >
                  {isOpen ? 'Ocultar detalle' : 'Ver detalle'}
                </button>

                {isOpen && (
                  <div className="order-history__details">
                    <div className="order-history__detail-head">
                      <div>
                        <h3>Detalle de la orden</h3>
                        <p>ID documento: {order.id}</p>
                        <p>Correo compra: {order.purchaseConfirmationEmailSent ? 'enviado' : 'no enviado / pendiente'}</p>
                      </div>
                      <div className="order-history__actions">
                        <button
                          type="button"
                          className="order-history__action"
                          disabled={busyOrderId === order.id}
                          onClick={() => handleGenerateQrs(order)}
                        >
                          {busyOrderId === order.id ? 'Procesando...' : 'Generar / actualizar QR'}
                        </button>
                        <button
                          type="button"
                          className="order-history__action order-history__action--secondary"
                          disabled={busyOrderId === order.id || !order.email}
                          onClick={() => handleResendEmail(order)}
                        >
                          Reenviar correo con QR
                        </button>
                      </div>
                    </div>

                    {!order.isPaid && (
                      <p className="order-history__notice order-history__notice--warning">
                        Esta orden no tiene pago acreditado. Podés generar QR manualmente desde el panel y reenviar el
                        correo si la orden tiene email cargado.
                      </p>
                    )}

                    <div className="order-history__products">
                      {(order.cartItems || []).map((item, index) => (
                        <article
                          key={item.id || index}
                          className={`order-history__product ${item.isUsed ? 'is-used' : 'is-active'}`}
                          style={{ opacity: order.isActive === false ? 0.55 : 1 }}
                        >
                          <div>
                            <h4>{item.model || 'Producto sin nombre'}</h4>
                            <p>Cantidad: {item.quantity || 0}</p>
                          </div>
                          <div className="order-history__product-meta">
                            <span>QR: {item.qrValue ? `${String(item.qrValue).slice(0, 12)}...` : 'Pendiente'}</span>
                            <span>Ingreso: {item.isUsed ? 'Usado' : 'Vigente'}</span>
                            <span>Fecha ingreso: {item.usedTimestamp ? String(item.usedTimestamp) : '—'}</span>
                          </div>
                        </article>
                      ))}
                    </div>

                    {orderNeedsQr(order) && (
                      <p className="order-history__notice order-history__notice--info">
                        Hay ítems sin QR guardado: usá &quot;Generar / actualizar QR&quot;.
                      </p>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {visibleOrders < filteredOrders.length && (
        <button type="button" onClick={loadMoreOrders} className="order-history__load-more">
          Cargar más ventas
        </button>
      )}
    </div>
  );
};

export default OrderList;
